import { marked } from 'marked';

// Configure marked with basic options - no custom renderer for now
marked.use({
  gfm: true,              // GitHub Flavored Markdown
  breaks: true,           // Convert \n to <br>
  pedantic: false,        // Don't be too strict
  headerIds: false,       // Don't add IDs to headers (security)
  mangle: false,          // Don't mangle email addresses
});

/**
 * Post-process HTML to add custom classes
 */
function addCustomClasses(html: string): string {
  return html
    // Add classes to lists
    .replace(/<ul>/g, '<ul class="markdown-list">')
    .replace(/<ol(\s+start="\d+")?>/g, '<ol class="markdown-list"$1>')
    .replace(/<li>/g, '<li class="markdown-list-item">')
    
    // Add classes to code blocks
    .replace(/<pre><code class="language-(\w+)">/g, (match, lang) => {
      return `
        <div class="code-block-container">
          <div class="code-block-header">
            <span class="code-block-language">${lang.toUpperCase()}</span>
          </div>
          <pre class="code-block"><code class="language-${lang}">`;
    })
    .replace(/<\/code><\/pre>/g, '</code></pre></div>')
    
    // Add classes to inline code
    .replace(/<code>/g, '<code class="inline-code">')
    
    // Add classes to tables
    .replace(/<table>/g, '<div class="table-container"><table class="markdown-table">')
    .replace(/<\/table>/g, '</table></div>')
    
    // Add classes to blockquotes
    .replace(/<blockquote>/g, '<blockquote class="markdown-blockquote">')
    
    // Add security attributes to links
    .replace(/<a href="([^"]+)"([^>]*)>/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="markdown-link"$2>');
}

/**
 * Advanced markdown to HTML converter using marked.js
 * Supports: GFM, tables, task lists, code blocks, blockquotes, and more
 */
export function markdownToHtml(markdown: string): string {
  // Input validation
  if (typeof markdown !== 'string') {
    console.error('markdownToHtml received non-string input:', typeof markdown, markdown);
    // Try to convert to string if possible
    if (markdown && typeof markdown === 'object') {
      if (typeof markdown.toString === 'function') {
        markdown = markdown.toString();
      } else {
        console.error('Cannot convert object to string:', markdown);
        return '';
      }
    } else {
      markdown = String(markdown || '');
    }
  }

  // Ensure we have a string
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  try {
    // Use basic marked rendering
    const html = marked(markdown);
    const htmlString = typeof html === 'string' ? html : '';
    
    // Add custom classes and styling
    return addCustomClasses(htmlString);
  } catch (error) {
    console.error('Markdown parsing error:', error);
    console.error('Input was:', typeof markdown, markdown);
    // Fallback to simple processing if marked.js fails
    return simpleMarkdownToHtml(markdown);
  }
}

/**
 * Fallback simple markdown processor (keep the old logic as backup)
 */
function simpleMarkdownToHtml(markdown: string): string {
  let html = markdown;

  // Convert headings (# ## ### #### ##### ######)
  html = html.replace(/^#{6}\s+(.+)$/gm, '<h6>$1</h6>');
  html = html.replace(/^#{5}\s+(.+)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>');

  // Convert bold (**text** or __text__)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

  // Convert italic (*text* or _text_)
  html = html.replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/(?<!_)_(?!_)([^_]+)_(?!_)/g, '<em>$1</em>');

  // Convert strikethrough (~~text~~)
  html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');

  // Convert line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');
  html = `<p>${html}</p>`;

  return html;
}

/**
 * Check if text contains markdown formatting
 * Enhanced to detect more patterns that marked.js can handle
 */
export function hasMarkdown(text: string): boolean {
  // Input validation
  if (typeof text !== 'string') {
    console.error('hasMarkdown received non-string input:', typeof text, text);
    // Try to convert to string
    if (text && typeof text === 'object') {
      if (typeof text.toString === 'function') {
        text = text.toString();
      } else {
        return false;
      }
    } else {
      text = String(text || '');
    }
  }

  if (!text || typeof text !== 'string') {
    return false;
  }

  const markdownPatterns = [
    /#{1,6}\s+.+/,                     // headings
    /\*\*.*?\*\*/,                     // bold
    /__.*?__/,                         // bold alt
    /(?<!\*)\*(?!\*).*?\*/,            // italic
    /(?<!_)_(?!_).*?_/,                // italic alt
    /~~.*?~~/,                         // strikethrough
    /\+\+.*?\+\+/,                     // underline (custom)
    /^[\s]*[-*+]\s+.+$/m,              // unordered lists (-, *, +)
    /^[\s]*\d+\.\s+.+$/m,              // ordered lists
    /^[\s]*[-*+]\s*\[[\sxX]\]\s+.+$/m, // task lists
    /```[\s\S]*?```/,                  // code blocks
    /`[^`\n]+`/,                       // inline code
    /^\s*>\s+.+$/m,                    // blockquotes
    /^\s*\|.+\|.*$/m,                  // tables
    /^\s*[-=]{3,}\s*$/m,               // horizontal rules
    /\[.*?\]\(.*?\)/,                  // links
    /!\[.*?\]\(.*?\)/,                 // images
  ];

  return markdownPatterns.some(pattern => pattern.test(text));
}